// src/pages/DeviceTypeManagement/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  LinkOff as UnassignedIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { deviceTypesApi, devicesApi } from '../../services/apiClient';
import DeviceTypeList from './DeviceTypeList';
import DeviceTypeDetails from './DeviceTypeDetails';
import CreateDeviceTypeDialog from './CreateDeviceTypeDialog';
import UnassignedDevicesTab from './UnassignedDevicesTab';

const DeviceTypeManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMainTab, setActiveMainTab] = useState(0);
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const loadDeviceTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deviceTypesApi.getAll({ search: searchTerm, limit: 100 });

      if (response.data.success) {
        setDeviceTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('[DeviceTypeManagement] Error loading device types:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to load device types',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [searchTerm, enqueueSnackbar]);

  const loadUnassignedCount = useCallback(async () => {
    try {
      const response = await devicesApi.getAll({ limit: 1000 });
      const allDevices = response?.data?.data || response?.data || [];

      // Find caro_agent type ID
      const caroAgentType = deviceTypes.find(dt =>
        dt.friendlyName?.toLowerCase() === 'caro_agent' ||
        dt.FrontEndName?.toLowerCase() === 'caro_agent'
      );
      const caroAgentId = caroAgentType?.id;

      // Device type IDs considered as "unassigned" or default (1, 2, and caro_agent)
      const unassignedTypeIds = [1, 2];
      if (caroAgentId) {
        unassignedTypeIds.push(caroAgentId);
      }

      // Helper to check if type is unassigned
      const isUnassignedType = (typeId) => {
        if (!typeId) return true;
        const numericId = typeof typeId === 'string' ? parseInt(typeId, 10) : typeId;
        return unassignedTypeIds.includes(numericId);
      };

      // Count unassigned devices
      const count = allDevices.filter(device => {
        const typeId = device.deviceTypeModel?.id || device.deviceType || device.deviceTypeId;
        return isUnassignedType(typeId);
      }).length;

      setUnassignedCount(count);
    } catch (error) {
      console.error('Error loading unassigned count:', error);
    }
  }, [deviceTypes]);

  useEffect(() => {
    if (isAdmin) {
      loadDeviceTypes();
    }
  }, [isAdmin, loadDeviceTypes]);

  useEffect(() => {
    if (deviceTypes.length > 0) {
      loadUnassignedCount();
    }
  }, [deviceTypes, loadUnassignedCount]);

  const handleDeviceTypeSelect = (deviceType) => {
    setSelectedDeviceType(deviceType);
  };

  const handleDeviceTypeCreated = (newDeviceType) => {
    setOpenCreateDialog(false);
    enqueueSnackbar('Device type created successfully', { variant: 'success' });
    loadDeviceTypes();
    setSelectedDeviceType(newDeviceType);
  };

  const handleDeviceTypeUpdated = () => {
    loadDeviceTypes();
    if (selectedDeviceType) {
      loadDeviceTypeDetails(selectedDeviceType.id);
    }
  };

  const loadDeviceTypeDetails = async (deviceTypeId) => {
    try {
      const response = await deviceTypesApi.getById(deviceTypeId);

      if (response.data.success) {
        setSelectedDeviceType(response.data.data);
      }
    } catch (error) {
      console.error('Error loading device type details:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to load device type details',
        { variant: 'error' }
      );
    }
  };

  const handleDeviceTypeDeleted = () => {
    setSelectedDeviceType(null);
    loadDeviceTypes();
    enqueueSnackbar('Device type deleted successfully', { variant: 'success' });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs value={activeMainTab} onChange={(e, v) => setActiveMainTab(v)}>
          <Tab
            icon={<CategoryIcon />}
            iconPosition="start"
            label="Device Types"
          />
          <Tab
            icon={
              <Badge badgeContent={unassignedCount} color="warning" max={99}>
                <UnassignedIcon />
              </Badge>
            }
            iconPosition="start"
            label="Unassigned Devices"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeMainTab === 0 ? (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Sidebar - Device Type List */}
          <Box
            sx={{
              width: 350,
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper'
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DevicesIcon color="primary" />
                <Typography variant="h6">
                  Device Type Management
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreateDialog(true)}
                  fullWidth
                  size="small"
                >
                  New Device Type
                </Button>
                <Button
                  variant="outlined"
                  onClick={loadDeviceTypes}
                  disabled={loading}
                  size="small"
                >
                  <RefreshIcon />
                </Button>
              </Box>
            </Box>

            {/* Device Type List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <DeviceTypeList
                deviceTypes={deviceTypes}
                selectedDeviceType={selectedDeviceType}
                onSelectDeviceType={handleDeviceTypeSelect}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
              />
            )}
          </Box>

          {/* Right Panel - Device Type Details */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedDeviceType ? (
              <DeviceTypeDetails
                deviceType={selectedDeviceType}
                onUpdate={handleDeviceTypeUpdated}
                onDelete={handleDeviceTypeDeleted}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 3
                }}
              >
                <DevicesIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a device type
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Choose a device type from the list to view and edit its details
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <UnassignedDevicesTab
            deviceTypes={deviceTypes}
            onDeviceUpdated={() => {
              loadUnassignedCount();
              loadDeviceTypes();
            }}
          />
        </Box>
      )}

      {/* Create Device Type Dialog */}
      <CreateDeviceTypeDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSuccess={handleDeviceTypeCreated}
      />
    </Box>
  );
};

export default DeviceTypeManagement;
